<template lang="pug">
main(
  role="main"
  v-loading="loading"
  :class="{ 'pl-main-menu-width': isWebAppMenu && !isEmbedded && isDesktop, 'embedded-layout': isEmbedded, 'no-padding-layout': noPadding }"
).pos-r.web-app-container
  .main-content_inner-wrap.web-app-menu.web-app-body(
    :class="{ 'menu-open': isWebAppMenu && !isDesktop }"
  )
    div
      web-app-menu(
        v-if="(isWebAppMenu || !isDesktop) && !isEmbedded"
        :items="WebAppMenuItems"
        :isWebAppMenu="isWebAppMenu"
      )
      div.overlay(v-if="isWebAppMenu && !isDesktop && !isEmbedded" @click="setMainMenuCollapse")
      .row.row-sm.align-items-center.mb-3(
        v-if="!isEmbedded"
        :class="{ 'has-gradient-header': hasGradient, 'white-header-mode': whiteMode, 'has-sidebar': isWebAppMenu && isDesktop }"
      )
        .col
          .d-flex.align-items-center
            fai(
              type="fal"
              icon="bars"
              @click="setMainMenuCollapse"
              v-if="!isDesktop && !isWebAppMenu"
              :class="{ 'white-icon': whiteMode }"
            ).size-28.me-2.min-size-28
            div(v-if="showLogo").mb-0
              img(
                src="/images/full-logo.png"
                :class="{ 'white-logo': hasGradient }"
              ).logo-image
            h3(v-else :class="{ 'white-text': whiteMode }").mb-0.mx-auto {{ title }}
        .col-auto
          slot(name="actions")
            //- actions slot

      .grid-2-cols(v-if="($slots['col-left'] || $slots['col-right']) && !isEmbedded")
        .col-has-child.main-col-left
          slot(name="col-left")
            //- col-left slot

        .col-has-child.main-col-right(v-if="$slots['col-right']")
          slot(name="col-right")
            //- col-right slot

      .col-has-child(v-else :class="{ 'p-0': false }")
        slot
</template>

<script lang="ts">
import {
  defineComponent, computed, ref, onMounted, onUnmounted, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import WebAppMenu from '../side-menu/WebAppMenu.vue';
import { WebAppMenuItems } from '../../../data/main-menu';

export default defineComponent({
  name: 'WebAppLayout',
  components: { WebAppMenu },
  props: {
    title: { type: String, default: '' },
    showLogo: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    noPadding: { type: Boolean, default: false },
    hasGradient: { type: Boolean, default: false },
    whiteMode: { type: Boolean, default: false },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const isWebAppMenu = ref(false); // Initialize as closed instead of open
    const loading = ref(false);
    const isDesktop = ref(false);

    const isWebRoute = computed(() => route.path.startsWith('/web'));
    const isEmbedded = computed(() => props.embedded || route.path.includes('/embed/'));

    function checkDesktop() {
      const prevIsDesktop = isDesktop.value;
      const prevIsWebAppMenu = isWebAppMenu.value;

      isDesktop.value = window.innerWidth >= 1024;
      const isCompleteProfile = route.path.startsWith('/auth/web/complete-profile');

      console.log('[WebAppLayout] 🔍 checkDesktop called:', {
        windowWidth: window.innerWidth,
        isDesktop: isDesktop.value,
        prevIsDesktop,
        isWebAppMenu: isWebAppMenu.value,
        prevIsWebAppMenu,
        route: route.path,
      });

      // Don't show menu in embedded mode
      if (isEmbedded.value) {
        isWebAppMenu.value = false;
        console.log('[WebAppLayout] 📱 Embedded mode - menu hidden');
        return;
      }

      if (isDesktop.value && (isWebRoute.value || isCompleteProfile)) {
        isWebAppMenu.value = true;
        console.log('[WebAppLayout] 💻 Desktop mode - menu shown');
      } else if (!isDesktop.value) {
        // Keep drawer closed on mobile by default
        isWebAppMenu.value = false;
        console.log('[WebAppLayout] 📱 Mobile mode - menu hidden by default');
      }

      // Log header and logo position
      setTimeout(() => {
        const header = document.querySelector('.row.row-sm.align-items-center.mb-3');
        const logo = document.querySelector('.logo-image');
        const hamburger = document.querySelector('.size-28.me-2.min-size-28');

        if (header) {
          const headerRect = header.getBoundingClientRect();
          console.log('[WebAppLayout] 📏 Header position:', {
            left: headerRect.left,
            width: headerRect.width,
            paddingLeft: window.getComputedStyle(header).paddingLeft,
          });
        }

        if (logo) {
          const logoRect = logo.getBoundingClientRect();
          console.log('[WebAppLayout] 🖼️ Logo position:', {
            left: logoRect.left,
            width: logoRect.width,
            height: logoRect.height,
          });
        }

        console.log('[WebAppLayout] 🍔 Hamburger visible:', !!hamburger);
      }, 100);
    }

    function setMainMenuCollapse() {
      isWebAppMenu.value = !isWebAppMenu.value;
      store.commit('shared/SET_MAIN_MENU_COLLAPSE', !isWebAppMenu.value);
    }

    // Watch for route changes to close drawer on mobile
    watch(() => route.path, () => {
      if (!isDesktop.value) {
        isWebAppMenu.value = false;
      }
    });

    // Watch for hasGradient prop changes
    watch(() => props.hasGradient, (newVal, oldVal) => {
      console.log('[WebAppLayout] 🎨 hasGradient changed:', oldVal, '->', newVal);
      if (newVal) {
        setTimeout(() => {
          const header = document.querySelector('.row.row-sm.align-items-center.mb-3');
          const hasClass = header?.classList.contains('has-gradient-header');
          console.log('[WebAppLayout] 🎯 Header has gradient class:', hasClass);
          console.log('[WebAppLayout] 🎯 Header width after gradient enabled:', header?.clientWidth, 'px');
        }, 50);
      }
    });

    onMounted(() => {
      console.log('[WebAppLayout] 🏗️ Layout mounted');
      console.log('[WebAppLayout] 📱 Viewport:', window.innerWidth, 'x', window.innerHeight, 'px');
      console.log('[WebAppLayout] ⚙️ Props:', {
        noPadding: props.noPadding,
        hasGradient: props.hasGradient,
        embedded: props.embedded,
        showLogo: props.showLogo,
        title: props.title,
      });
      console.log('[WebAppLayout] 🎯 Initial state:', {
        isDesktop: isDesktop.value,
        isWebAppMenu: isWebAppMenu.value,
        isWebRoute: isWebRoute.value,
        isEmbedded: isEmbedded.value,
      });
      checkDesktop();
      window.addEventListener('resize', checkDesktop);

      // Debug actual widths and overflow
      setTimeout(() => {
        const container = document.querySelector('.web-app-container');
        const innerWrap = document.querySelector('.main-content_inner-wrap');
        const header = document.querySelector('.row.row-sm.align-items-center.mb-3');
        const hasGradientClass = header?.classList.contains('has-gradient-header');

        console.log('[WebAppLayout] 📐 Actual widths:');
        console.log('  - Viewport:', window.innerWidth, 'px');
        console.log('  - Container:', container?.clientWidth || 'N/A', 'px (max 1400px)');
        console.log('  - Container overflow-x:', window.getComputedStyle(container as Element).overflowX);
        console.log('  - Inner wrap:', innerWrap?.clientWidth || 'N/A', 'px');
        console.log('  - Inner wrap overflow-x:', window.getComputedStyle(innerWrap as Element).overflowX);
        console.log('  - Header:', header?.clientWidth || 'N/A', 'px', hasGradientClass ? '(full-width mode)' : '(constrained)');

        if (header) {
          const headerStyles = window.getComputedStyle(header);
          console.log('[WebAppLayout] 🎯 Header styles:');
          console.log('  - margin-left:', headerStyles.marginLeft);
          console.log('  - margin-right:', headerStyles.marginRight);
          console.log('  - padding-left:', headerStyles.paddingLeft);
          console.log('  - padding-right:', headerStyles.paddingRight);
          console.log('  - position:', headerStyles.position);
        }
      }, 100);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkDesktop);
    });

    return {
      // getter
      me: computed(() => store.getters['users/me']),

      // data
      user: {},
      isWebAppMenu,
      isDesktop,
      isWebRoute,
      isEmbedded,
      WebAppMenuItems,
      setMainMenuCollapse,
      loading,
    };
  },
});
</script>
<style lang="scss">
// Unified padding variables for all web app pages
:root {
  --web-app-padding-mobile: 24px;
  --web-app-padding-tablet: 40px;
  --web-app-padding-desktop: 64px;
}

// Override global grey background for web app
body {
  background: #ffffff !important;
}

#app {
  background: #ffffff !important;
}

.web-app-container {
    background: #fff;
    overflow-x: hidden;
    max-width: 1400px; // 1100px content + 300px sidebar (reduced by 200px)
    margin: 0; // Left-aligned, not centered

    // Allow overflow when header needs to break out to full width
    &:has(.has-gradient-header) {
        overflow-x: visible;
    }

    .main-content_inner-wrap {
        background: #fff;
        padding: 0;
        overflow-x: hidden;

        // Allow overflow when header needs to break out to full width
        &:has(.has-gradient-header) {
            overflow-x: visible;
        }
    }
}
.overlay {
    position: fixed;
    top: 0;
    right: 0;
    width: 100dvw;
    height: 100%;
    cursor: pointer;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.4);
}
.logo-image {
    height: 28px;
    display: block;

    &.white-logo {
      filter: brightness(0) invert(1);
    }
}

@media (min-width: 1024px) {
  .overlay {
    display: none;
  }

  .logo-image {
    height: 33px;
  }
}

/* Embedded layout styles */
.embedded-layout {
  .main-content_inner-wrap {
    padding: 0;
  }

  .col-has-child {
    padding: 0;
  }

  /* Ensure embedded content takes full width */
  max-width: 100%;
  padding: 0;

  /* Remove any default margins/padding for embedded context */
  .web-app-body {
    padding: 0;
    margin: 0;
  }
}

/* No padding layout styles (for static pages and search) */
.no-padding-layout {
  .main-content_inner-wrap {
    padding: 0;
  }

  /* Keep padding for the header row (hamburger/logo/title) */
  .row.row-sm.align-items-center.mb-3 {
    padding: 20px 24px 0;

    .col {
      min-width: 0; // Prevent flex item from overflowing
    }

    .d-flex.align-items-center {
      gap: 0; // Remove any gap that might cause shifting
      justify-content: flex-start; // Always align to left
    }

    @media (min-width: 768px) {
      padding: 24px 40px 0;
    }

    @media (min-width: 1024px) {
      padding: 24px 64px 0;
    }

    &.has-gradient-header {
      background-color: #225c6b;
      padding: 20px 24px;
      margin-bottom: 0 !important;
      position: relative;
      z-index: 10; // Above the gradient (z-index: 0)

      // Break out of container to full viewport width
      margin-left: calc(-50vw + 50%);
      margin-right: calc(-50vw + 50%);
      padding-left: 24px;
      padding-right: 24px;

      @media (min-width: 768px) {
        padding: 20px 40px;
        padding-left: 40px;
        padding-right: 40px;

      }

      @media (min-width: 1024px) {
        padding: 20px 64px;
        padding-right: 64px;

        // When sidebar is visible, add padding to account for sidebar width
        // The calc accounts for: sidebar width (300px) + the shift from
        // margin-left calc(-50vw + 50%)
        &.has-sidebar {
          padding-left: calc(50px + 50vw - 50%);
        }
      }
    }

    // White mode for logo and icons when over gradient
    &.white-header-mode {
      .logo-image {
        filter: brightness(0) invert(1);
      }

      .white-icon {
        color: white !important;
      }

      .white-text {
        color: white !important;
      }
    }
  }

  .col-has-child {
    padding: 0;
    margin: 0;
  }

  /* Ensure content takes full width */
  .web-app-body {
    padding: 0;
    margin: 0;
  }

  /* Remove any gap between gradient header and content */
  .row.row-sm.align-items-center.mb-3.has-gradient-header + .grid-2-cols,
  .row.row-sm.align-items-center.mb-3.has-gradient-header + .col-has-child {
    margin-top: 0;
  }

  /* Ensure wrapper div doesn't add spacing */
  .main-content_inner-wrap > div {
    margin: 0;
  }
}

/* White mode styles */
.white-icon {
  color: white !important;
}

.white-text {
  color: white !important;
}
</style>
